import React, { useState, useEffect } from "react";

const NimGame = () => {
  const [piles, setPiles] = useState([1, 3, 5, 7]);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [gameStatus, setGameStatus] = useState("playing");
  const [winner, setWinner] = useState(null);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [displayMode, setDisplayMode] = useState("matches"); // 'matches' or 'binary'
  const [binaryShowMode, setBinaryShowMode] = useState("matches"); // 'matches' or 'code'
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [showStrategy, setShowStrategy] = useState(false);

  // 计算异或和
  const calculateNimSum = (pilesArray) => {
    return pilesArray.reduce((sum, pile) => sum ^ pile, 0);
  };

  // 计算每一位的异或结果（用于灯泡显示）
  const calculateBitXor = () => {
    const maxBits = Math.max(
      ...piles.map((pile) => pile.toString(2).length),
      1
    );
    const bitXorResults = [];

    for (let bit = 0; bit < maxBits; bit++) {
      let count = 0;
      piles.forEach((pile) => {
        if ((pile >> bit) & 1) {
          count++;
        }
      });
      bitXorResults.push(count % 2 === 1);
    }

    return bitXorResults;
  };

  // 找到获胜策略
  const findWinningMove = () => {
    const nimSum = calculateNimSum(piles);
    if (nimSum === 0) return null; // 已经平衡，没有必胜策略

    // 找到最高位的不平衡位
    const highestBit = Math.floor(Math.log2(nimSum));

    // 找到包含这个最高位的堆
    for (let i = 0; i < piles.length; i++) {
      if (piles[i] & (1 << highestBit)) {
        const targetValue = piles[i] ^ nimSum;
        if (targetValue < piles[i]) {
          return {
            pileIndex: i,
            currentValue: piles[i],
            targetValue: targetValue,
            remove: piles[i] - targetValue,
          };
        }
      }
    }
    return null;
  };

  // 检查游戏是否结束
  useEffect(() => {
    const totalSticks = piles.reduce((sum, pile) => sum + pile, 0);
    if (totalSticks === 0) {
      setGameStatus("finished");
      setWinner(currentPlayer === 1 ? 2 : 1);
    }
  }, [piles, currentPlayer]);

  // 显示错误提示
  const showErrorMessage = (message) => {
    setErrorMessage(message);
    setShowError(true);
    setTimeout(() => setShowError(false), 3000);
  };

  // 处理普通模式火柴棒点击
  const handleMatchClick = (pileIndex, matchIndex) => {
    if (gameStatus === "finished") return;

    if (
      selectedMatches.length > 0 &&
      selectedMatches[0].pileIndex !== pileIndex
    ) {
      showErrorMessage("只能从同一行中拿取火柴棒！请先取消当前选择。");
      return;
    }

    const matchKey = `${pileIndex}-${matchIndex}`;
    const isSelected = selectedMatches.some(
      (m) => `${m.pileIndex}-${m.matchIndex}` === matchKey
    );

    if (isSelected) {
      setSelectedMatches(
        selectedMatches.filter(
          (m) => `${m.pileIndex}-${m.matchIndex}` !== matchKey
        )
      );
    } else {
      setSelectedMatches([...selectedMatches, { pileIndex, matchIndex }]);
    }
  };

  // 处理二进制模式火柴棒点击
  const handleBinaryMatchClick = (pileIndex, bitIndex, matchIndex) => {
    if (gameStatus === "finished") return;

    if (
      selectedMatches.length > 0 &&
      selectedMatches[0].pileIndex !== pileIndex
    ) {
      showErrorMessage("只能从同一行中拿取物品！请先取消当前选择。");
      return;
    }

    const matchKey = `${pileIndex}-${bitIndex}-${matchIndex}`;
    const isSelected = selectedMatches.some(
      (m) =>
        m.pileIndex === pileIndex &&
        m.bitIndex === bitIndex &&
        m.matchIndex === matchIndex
    );

    if (isSelected) {
      setSelectedMatches(
        selectedMatches.filter(
          (m) =>
            !(
              m.pileIndex === pileIndex &&
              m.bitIndex === bitIndex &&
              m.matchIndex === matchIndex
            )
        )
      );
    } else {
      setSelectedMatches([
        ...selectedMatches,
        { pileIndex, bitIndex, matchIndex },
      ]);
    }
  };

  // 确认移动
  const confirmMove = () => {
    if (selectedMatches.length === 0) {
      showErrorMessage("请至少选择一个物品！");
      return;
    }

    const pileIndex = selectedMatches[0].pileIndex;
    const amount = selectedMatches.length;

    const newPiles = [...piles];
    newPiles[pileIndex] = Math.max(0, newPiles[pileIndex] - amount);
    setPiles(newPiles);
    setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
    setSelectedMatches([]);
  };

  // 重新开始游戏
  const resetGame = () => {
    setPiles([1, 3, 5, 7]);
    setCurrentPlayer(1);
    setGameStatus("playing");
    setWinner(null);
    setSelectedMatches([]);
    setShowStrategy(false);
  };

  // 渲染二进制模式
  const renderBinaryMode = () => {
    const maxBits = Math.max(
      ...piles.map((pile) => pile.toString(2).length),
      1
    );
    const bitXorResults = calculateBitXor();
    const winningMove = findWinningMove();

    return (
      <div className="p-6">
        {/* 显示模式切换 */}
        <div className="flex justify-center mb-6">
          <div className="bg-gray-100 rounded-lg p-1 flex gap-1">
            <button
              onClick={() => setBinaryShowMode("matches")}
              className={`px-4 py-2 rounded transition-all ${
                binaryShowMode === "matches"
                  ? "bg-white text-blue-600 shadow"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              🔥 火柴棒显示
            </button>
            <button
              onClick={() => setBinaryShowMode("code")}
              className={`px-4 py-2 rounded transition-all ${
                binaryShowMode === "code"
                  ? "bg-white text-blue-600 shadow"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              💻 01编码显示
            </button>
          </div>
        </div>

        {/* 位权标题 - 使用更直观的描述 - 从高位到低位 */}
        <div className="flex justify-center items-center mb-6">
          <div className="w-20 text-center text-sm font-bold text-gray-600">
            行
          </div>
          {Array.from({ length: maxBits }, (_, idx) => {
            const bitIndex = maxBits - 1 - idx; // 反转顺序
            const value = Math.pow(2, bitIndex);
            return (
              <div key={bitIndex} className="w-24 text-center">
                <div className="text-sm font-bold text-gray-700 mb-1">
                  {value === 1 ? "1个一组" : `${value}个一组`}
                </div>
              </div>
            );
          })}
        </div>

        {/* 每一行的二进制分解 */}
        {piles.map(
          (pile, pileIndex) =>
            pile > 0 && (
              <div
                key={pileIndex}
                className="flex justify-center items-center mb-4 relative"
              >
                {/* 策略提示 - 使用绝对定位不影响布局 */}
                {showStrategy &&
                  winningMove &&
                  winningMove.pileIndex === pileIndex && (
                    <div className="absolute left-0 top-0 bottom-0 flex items-center pointer-events-none">
                      <div className="bg-green-500 text-white px-3 py-1 rounded-r-lg shadow-lg text-xs font-bold animate-pulse">
                        👉 从这行拿{winningMove.remove}个
                      </div>
                    </div>
                  )}

                <div
                  className={`flex justify-center items-center p-2 rounded-lg transition-all ${
                    showStrategy &&
                    winningMove &&
                    winningMove.pileIndex === pileIndex
                      ? "bg-green-100 border-2 border-green-500"
                      : ""
                  }`}
                >
                  <div className="w-20 text-center text-sm font-medium text-gray-600">
                    第{pileIndex + 1}行<br />({pile})
                  </div>
                  {Array.from({ length: maxBits }, (_, idx) => {
                    const bitIndex = maxBits - 1 - idx; // 反转顺序，从高位到低位
                    const bitValue = Math.pow(2, bitIndex);
                    const hasBit = (pile >> bitIndex) & 1;

                    if (binaryShowMode === "code") {
                      // 01编码显示
                      return (
                        <div
                          key={bitIndex}
                          className="w-24 flex justify-center"
                        >
                          <div
                            className={`w-20 h-20 border-2 rounded-lg flex items-center justify-center text-4xl font-bold ${
                              hasBit
                                ? "bg-blue-50 border-blue-400 text-blue-600"
                                : "bg-gray-50 border-gray-300 text-gray-400"
                            }`}
                          >
                            {hasBit ? "1" : "0"}
                          </div>
                        </div>
                      );
                    } else {
                      // 火柴棒显示
                      return (
                        <div
                          key={bitIndex}
                          className="w-24 flex justify-center"
                        >
                          <div className="w-20 h-20 border-2 border-gray-400 rounded-lg bg-gray-50 flex flex-wrap justify-center items-center p-1">
                            {hasBit ? (
                              Array.from(
                                { length: bitValue },
                                (_, matchIndex) => {
                                  const isSelected = selectedMatches.some(
                                    (m) =>
                                      m.pileIndex === pileIndex &&
                                      m.bitIndex === bitIndex &&
                                      m.matchIndex === matchIndex
                                  );

                                  return (
                                    <div
                                      key={matchIndex}
                                      className={`w-2 h-6 rounded-sm cursor-pointer transition-all transform hover:scale-110 m-0.5 ${
                                        isSelected ? "ring-1 ring-blue-500" : ""
                                      }`}
                                      style={{
                                        background: isSelected
                                          ? "linear-gradient(to bottom, #3b82f6 0%, #1d4ed8 100%)"
                                          : "linear-gradient(to bottom, #f59e0b 0%, #d97706 100%)",
                                      }}
                                      onClick={() =>
                                        handleBinaryMatchClick(
                                          pileIndex,
                                          bitIndex,
                                          matchIndex
                                        )
                                      }
                                    >
                                      <div
                                        className={`w-0.5 h-0.5 rounded-full mx-auto mt-0.5 ${
                                          isSelected
                                            ? "bg-blue-200"
                                            : "bg-red-500"
                                        }`}
                                      ></div>
                                    </div>
                                  );
                                }
                              )
                            ) : (
                              <div className="text-gray-400 text-xs">无</div>
                            )}
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              </div>
            )
        )}

        {/* 异或结果 */}
        <div className="flex justify-center items-center mt-8 pt-6 border-t-2 border-gray-300">
          <div className="w-20 text-center text-sm font-medium text-gray-600">
            异或
            <br />
            结果
          </div>
          {binaryShowMode === "code"
            ? // 01编码显示 - 从高位到低位
              Array.from({ length: maxBits }, (_, idx) => {
                const bitIndex = maxBits - 1 - idx; // 反转顺序
                const isOdd = bitXorResults[bitIndex];
                return (
                  <div key={bitIndex} className="w-24 flex justify-center">
                    <div className="relative">
                      <div
                        className={`w-20 h-20 border-4 rounded-lg flex items-center justify-center text-4xl font-bold transition-all ${
                          isOdd
                            ? "bg-red-50 border-red-500 text-red-600"
                            : "bg-green-50 border-green-500 text-green-600"
                        }`}
                      >
                        {isOdd ? "1" : "0"}
                      </div>
                      <div
                        className={`text-xs text-center mt-2 font-bold ${
                          isOdd ? "text-red-600" : "text-green-600"
                        }`}
                      >
                        {isOdd ? "不平衡" : "平衡"}
                      </div>
                    </div>
                  </div>
                );
              })
            : // 灯泡显示 - 从高位到低位
              Array.from({ length: maxBits }, (_, idx) => {
                const bitIndex = maxBits - 1 - idx; // 反转顺序
                const isOdd = bitXorResults[bitIndex];
                return (
                  <div key={bitIndex} className="w-24 flex justify-center">
                    <div className="relative">
                      <div
                        className={`w-16 h-16 rounded-full border-4 transition-all duration-500 flex items-center justify-center ${
                          isOdd
                            ? "bg-yellow-300 border-yellow-500 shadow-lg animate-pulse"
                            : "bg-gray-200 border-gray-400"
                        }`}
                        style={{
                          boxShadow: isOdd
                            ? "0 0 25px rgba(255, 255, 0, 0.8), inset 0 0 25px rgba(255, 255, 0, 0.4)"
                            : "inset 0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      ></div>
                      <div className="text-xs text-center mt-2 font-bold">
                        {isOdd ? "不平衡" : "平衡"}
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>

        {/* 平衡恢复演示 */}
        {winningMove && (
          <div className="mt-8">
            <div className="flex justify-center mb-4">
              <button
                onClick={() => setShowStrategy(!showStrategy)}
                className={`px-6 py-3 rounded-lg font-bold transition-all ${
                  showStrategy
                    ? "bg-green-500 text-white hover:bg-green-600"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
              >
                {showStrategy ? "🔒 隐藏获胜策略" : "💡 显示如何恢复平衡"}
              </button>
            </div>

            {showStrategy && (
              <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-400">
                <h4 className="font-bold text-green-800 mb-3 text-lg">
                  🎯 平衡恢复策略
                </h4>
                <div className="text-sm space-y-2">
                  <p className="font-semibold text-green-700">
                    ✅ 当前局面不平衡，有必胜策略！
                  </p>
                  <div className="bg-white p-3 rounded border-l-4 border-green-500">
                    <p className="font-bold text-gray-800 mb-2">
                      🔧 恢复平衡的方法：
                    </p>
                    <ul className="space-y-1 ml-4">
                      <li>
                        📍 从{" "}
                        <span className="font-bold text-blue-600">
                          第{winningMove.pileIndex + 1}行
                        </span>{" "}
                        拿走
                        <span className="font-bold text-red-600">
                          {" "}
                          {winningMove.remove}个
                        </span>{" "}
                        火柴棒
                      </li>
                      <li>
                        📊 第{winningMove.pileIndex + 1}行会从
                        <span className="font-bold">
                          {" "}
                          {winningMove.currentValue}个
                        </span>{" "}
                        变成
                        <span className="font-bold">
                          {" "}
                          {winningMove.targetValue}个
                        </span>
                      </li>
                      <li className="text-green-600 font-semibold">
                        ✨ 这样所有位都会变成"平衡"状态！
                      </li>
                    </ul>
                  </div>
                  <div className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-500 mt-3">
                    <p className="text-xs text-gray-700">
                      <strong>原理：</strong>
                      找到最高的不平衡位，从包含这一位的某一行进行调整。
                      通过异或运算，可以精确计算出需要拿取的数量，使所有位都达到平衡（偶数个1）。
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {!winningMove && (
          <div className="mt-8 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
            <h4 className="font-bold text-blue-800 mb-2">🎯 当前局面分析</h4>
            <div className="text-sm text-blue-700">
              <p className="font-semibold">🟢 当前是平衡态（异或和为0）</p>
              <p className="mt-2">
                在这种情况下，无论你怎么走，都会打破平衡。如果对手理解这个规律，
                他就能通过合理移动重新恢复平衡，最终获胜。
              </p>
              <p className="mt-2 text-xs text-gray-600">
                💡 提示：如果对手失误没有恢复平衡，你就有机会了！
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // 渲染普通火柴棒模式
  const renderNormalMode = () => {
    return (
      <div className="max-w-2xl mx-auto">
        {piles.map(
          (pile, pileIndex) =>
            pile > 0 && (
              <div
                key={pileIndex}
                className="flex justify-center items-center gap-2 mb-4"
              >
                <div className="w-16 text-right text-sm font-medium text-gray-600">
                  第{pileIndex + 1}行:
                </div>
                <div className="flex justify-center gap-1 flex-wrap">
                  {Array.from({ length: pile }, (_, matchIndex) => {
                    const isSelected = selectedMatches.some(
                      (m) =>
                        m.pileIndex === pileIndex && m.matchIndex === matchIndex
                    );

                    return (
                      <div
                        key={matchIndex}
                        className={`w-4 h-16 rounded-sm cursor-pointer transition-all transform hover:scale-105 ${
                          isSelected ? "ring-2 ring-blue-500 ring-offset-1" : ""
                        }`}
                        style={{
                          background: isSelected
                            ? "linear-gradient(to bottom, #3b82f6 0%, #1d4ed8 100%)"
                            : "linear-gradient(to bottom, #f59e0b 0%, #d97706 100%)",
                        }}
                        onClick={() => handleMatchClick(pileIndex, matchIndex)}
                      >
                        <div
                          className={`w-3 h-3 rounded-full mx-auto mt-1 ${
                            isSelected ? "bg-blue-200" : "bg-red-500"
                          }`}
                        ></div>
                      </div>
                    );
                  })}
                </div>
                <div className="w-16 text-left text-sm text-gray-500">
                  {pile}个
                </div>
              </div>
            )
        )}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-6 bg-green-800 min-h-screen">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-green-800">
            NIM 游戏 - 二进制探究
          </h1>
          {/* 显示模式切换 */}
          <div className="flex gap-2">
            <button
              onClick={() => setDisplayMode("matches")}
              className={`px-4 py-2 rounded ${
                displayMode === "matches"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              火柴棒模式
            </button>
            <button
              onClick={() => setDisplayMode("binary")}
              className={`px-4 py-2 rounded ${
                displayMode === "binary"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              二进制模式
            </button>
          </div>
        </div>

        {/* 游戏状态 */}
        <div className="text-center mb-6">
          {gameStatus === "playing" ? (
            <div>
              <p className="text-2xl mb-2">
                轮到:{" "}
                <span className="font-bold text-blue-600">
                  玩家 {currentPlayer}
                </span>
              </p>
              {selectedMatches.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  已选择第{selectedMatches[0].pileIndex + 1}行的{" "}
                  {selectedMatches.length} 个物品
                </p>
              )}
            </div>
          ) : (
            <div className="text-3xl font-bold text-green-600">
              🎉 玩家 {winner} 获胜！
            </div>
          )}
        </div>

        {/* 游戏区域 */}
        <div className="p-6 bg-green-50 rounded-lg mb-6">
          {displayMode === "binary" ? renderBinaryMode() : renderNormalMode()}
        </div>

        {/* 错误提示 */}
        {showError && (
          <div className="mb-4 flex justify-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
              <span className="text-xl">⚠️</span>
              <span className="text-sm font-medium">{errorMessage}</span>
            </div>
          </div>
        )}

        {/* 操作按钮 */}
        {gameStatus === "playing" && selectedMatches.length > 0 && (
          <div className="flex justify-center gap-4 mb-6">
            <button
              onClick={confirmMove}
              className="px-8 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-bold text-lg"
            >
              确认拿取 ({selectedMatches.length} 个)
            </button>
            <button
              onClick={() => setSelectedMatches([])}
              className="px-8 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-lg"
            >
              取消选择
            </button>
          </div>
        )}

        {/* 重新开始 */}
        <div className="text-center mb-6">
          <button
            onClick={resetGame}
            className="px-8 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-bold text-lg"
          >
            重新开始游戏
          </button>
        </div>

        {/* 游戏规则说明 */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="font-bold mb-3 text-lg">📋 游戏规则</h3>
          {displayMode === "binary" ? (
            <div className="text-sm space-y-2">
              <p>
                <strong>二进制分解模式：</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>每一行按照"1个一组"、"2个一组"、"4个一组"等方式分解</li>
                <li>可以切换"火柴棒显示"和"01编码"两种显示方式</li>
                <li>下方显示每一列的异或结果（平衡/不平衡）</li>
                <li>
                  <strong>获胜秘诀：让所有列都显示"平衡"（全部为0）！</strong>
                </li>
                <li>点击"显示如何恢复平衡"可以看到必胜策略</li>
              </ul>
            </div>
          ) : (
            <ul className="text-sm space-y-1 list-disc list-inside">
              <li>点击火柴棒来选择，选中的会变蓝色</li>
              <li>每次只能从同一行中拿取</li>
              <li>每次至少要取1个，可以取完整行</li>
              <li>取走最后一个火柴棒的玩家获胜</li>
            </ul>
          )}
        </div>
      </div>

      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default NimGame;
